import { useKeenSlider } from 'keen-slider/react';
import React, { Children, ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';

import { useDebounce } from '../../../hooks/useDebounce';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { ArrowLeft, ArrowRight } from '../../Icon';

type Props = {
  children: ReactNode;
  isSlider?: boolean;
  isSingleOption?: boolean;
  isColorPicker?: boolean;
  isPanel?: boolean;
  isColorArrow?: boolean;
  isBracketOption?: boolean;
  isHoneyComb?: boolean;
};

const Wrapper = styled.div<{
  isSlider?: boolean;
  isPanel?: boolean;
  isSingleOption?: boolean;
  isBracketOption?: boolean;
  isHoneyComb?: boolean;
}>`
  display: flex;
  overflow: hidden;
  margin: 0;
  padding: 4px;
  width: 100%;
  max-width: 100%;

  ${({ isSlider }) => {
    if (isSlider) {
      return `
      max-width: 100%;
      padding: 0;
    `;
    }
    return `
        min-width: 300px;
        max-width: 65%;
        flex-wrap: wrap
      `;
  }};

  ${({ isSingleOption }) => {
    if (isSingleOption) {
      return `
      justify-content: center;
    `;
    }
  }};

  ${({ isPanel }) => {
    if (isPanel) {
      return `
      overflow-x: scroll;
      -webkit-overflow-scrolling: touch;
      max-width: 100%;
      flex-wrap: initial;
      &::-webkit-scrollbar {
        display: none;
      }
    `;
    }
  }};

  ${({ isBracketOption }) => {
    if (isBracketOption) return `
      min-width: 100% !important;
      justify-content: flex-start;
      gap: 10px;
    `
  }}

  @media (min-width: 768px) {
    flex-wrap: wrap;
    width: 100%;
    margin: 0 auto;
    ${({ isBracketOption }) => {
      if (isBracketOption) return `justify-content: flex-start;`
      else return `justify-content: center;`
    }}
  }

  @media (min-width: 1441px) {
    ${({ isBracketOption, isHoneyComb }) => {
      if (isBracketOption) return `margin: 5px 0px;`
      else if (isHoneyComb) return `margin-top: 0px; justify-content: flex-start;`
      else return `margin-top: 30px;`
    }}
  }
  
  @media (max-width: 1440px) {
    ${({ isBracketOption, isHoneyComb }) => {
      if (isBracketOption) return `margin: 5px 0px;`
      else if (isHoneyComb) return `margin-top: 0px; justify-content: flex-start;`
      else return `margin-top: 40px;`
    }}
  }

  @media (max-width: 767px) {
    min-width: auto;
    max-width: 350px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    ${({ isBracketOption, isHoneyComb }) => {
      if (isBracketOption) return `margin: 5px 0px;`
      else if (isHoneyComb) return `margin-top: 0%; position: relative;`
      else return `margin-top: 20%;`
    }}
  }
  
  @media (max-width: 374px) {
    ${({ isBracketOption }) => {
    if (isBracketOption) {
      return `gap: 8px;`;
    }}
  }
`;

const StyledButtonsWrpper = styled.div`
  @media (min-width: 768px) {
    display: none;
  }
`;

const StyledLeftButton = styled.div<{ disabled?: boolean }>`
  width: 44px;
  z-index: 9;
  padding: 0;
  border: none;
  background: rgba(255, 255, 255, 0.7);
  position: absolute;
  left: 0;
  top: 13%;
  display: block;
  cursor: ${({ disabled }) => {
    return disabled ? `not-allowed; opacity: 0.5;` : `pointer`;
  }};

  @media (max-width: 767px) {
    top: -4px;
  }
`;

const StyledRightButton = styled.div<{ disabled?: boolean }>`
  width: 44px;
  z-index: 9;
  padding: 0;
  border: none;
  background: rgba(255, 255, 255, 0.7);
  position: absolute;
  right: 0;
  top: 13%;
  display: block;
  cursor: ${({ disabled }) => {
    return disabled ? `not-allowed; opacity: 0.5;` : `pointer`;
  }};

  @media (max-width: 767px) {
    top: -5px;
  }
`;

// This can potentially change to be a slider too
const Selector = ({
  children,
  isSlider,
  isPanel = false,
  isColorPicker,
  isColorArrow = false,
  isSingleOption,
  isBracketOption,
  isHoneyComb,
}: Props) => {
  const windowSize = useWindowSize();
  const [currentSlide, setCurrentSlide] = useState(0);
  const { width: windowWidth, height: windowHeight } = useDebounce(
    windowSize,
    200,
  );

  const childCount = Children.toArray(children).length;

  let slidesPerView: number;
  if (isColorPicker) {
    slidesPerView =
      windowWidth > 767
        ? Math.floor((windowWidth * 0.45) / 76)
        : Math.floor(windowWidth / 60);
  } else {
    slidesPerView = Math.max(Math.floor((windowWidth - 56) / 159), 2);
  }

  if (windowWidth <= 767 && !isColorPicker) {
    slidesPerView = Math.min(childCount || 1, 3);
  }

  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    spacing: 4,
    slidesPerView: slidesPerView,
    loop: false,
    controls: true,
    mode: 'free',
    vertical: false,
    slides: '.keen-slider-selector__slide',
    breakpoints: {
      '(min-width: 768px)': {
        controls: false,
      },
      '(max-width: 767px)': {
        slidesPerView: slidesPerView,
      },
    },
    slideChanged(s) {
      setCurrentSlide(s.details().relativeSlide);
    },
  });

  // Re-jig slider if window size changes
  useEffect(() => {
    if (slider === null || !sliderRef.current) return;
    slider.resize();

    if (windowWidth < 768) return;
    slider.destroy();
  }, [windowWidth, windowHeight, children, slider, sliderRef]);

  if (isSlider) {
    return (
      <Wrapper
        isSlider={true}
        ref={sliderRef}
        isSingleOption={isSingleOption}
        className="keen-slider"
        isHoneyComb={isHoneyComb}
      >
        {children}
        {isColorArrow && slider !== null && (
          <StyledButtonsWrpper>
            <StyledLeftButton
              onClick={e => slider.prev()}
              disabled={currentSlide === 0}
            >
              <ArrowLeft />
            </StyledLeftButton>
            <StyledRightButton
              onClick={e => slider.next()}
              disabled={currentSlide === slider.details().size - slidesPerView}
            >
              <ArrowRight />
            </StyledRightButton>
          </StyledButtonsWrpper>
        )}
      </Wrapper>
    );
  }

  if (isPanel) {
    return (
      <Wrapper isPanel={true} isSlider={false} isSingleOption={isSingleOption}>
        {children}
      </Wrapper>
    );
  }

  return (
    <Wrapper isSlider={false} isSingleOption={isSingleOption} isBracketOption={isBracketOption}>
      {children}
    </Wrapper>
  );
};

export default Selector;
